<template>
  <div class="plugin-builder">
    <div class="builder-header">
      <h2>{{ $tt('插件开发工具') }}</h2>
      <p>{{ $tt('轻松创建自定义插件，无需深入了解复杂的技术细节') }}</p>
    </div>

    <!-- 步骤导航 -->
    <div class="steps-container">
      <div class="steps">
        <div :class="['step', { active: currentStep === 1, completed: currentStep > 1 }]">
          <span class="step-number">1</span>
          <span class="step-title">{{ $tt('选择类型') }}</span>
        </div>
        <div :class="['step', { active: currentStep === 2, completed: currentStep > 2 }]">
          <span class="step-number">2</span>
          <span class="step-title">{{ $tt('基本信息') }}</span>
        </div>
        <div :class="['step', { active: currentStep === 3, completed: currentStep > 3 }]">
          <span class="step-number">3</span>
          <span class="step-title">{{ $tt('配置项') }}</span>
        </div>
        <div :class="['step', { active: currentStep === 4, completed: currentStep > 4 }]">
          <span class="step-number">4</span>
          <span class="step-title">{{ $tt('生成预览') }}</span>
        </div>
      </div>
    </div>

    <!-- 步骤内容 -->
    <div class="step-content">
      <!-- 步骤1: 选择插件类型 -->
      <div v-if="currentStep === 1" class="step-panel">
        <h3>{{ $tt('选择插件类型') }}</h3>
        
        <!-- 从模板创建 -->
        <div class="creation-method">
          <h4>{{ $tt('从模板创建') }}</h4>
          <div class="template-grid">
            <div 
              v-for="template in templates" 
              :key="template.id"
              :class="['template-card', { selected: selectedTemplate === template.id }]"
              @click="selectTemplate(template.id)"
            >
              <div class="template-icon">
                <i :class="getTemplateIcon(template.plugin_type)"></i>
              </div>
              <h5>{{ template.name }}</h5>
              <p>{{ template.description }}</p>
              <div class="template-type">{{ template.category }}</div>
            </div>
          </div>
        </div>

        <!-- 自定义创建 -->
        <div class="creation-method">
          <h4>{{ $tt('自定义创建') }}</h4>
          <div class="plugin-types">
            <div 
              v-for="type in pluginTypes" 
              :key="type.value"
              :class="['type-card', { selected: pluginForm.plugin_type === type.value && !selectedTemplate }]"
              @click="selectPluginType(type.value)"
            >
              <div class="type-icon">
                <i :class="getPluginTypeIcon(type.value)"></i>
              </div>
              <h5>{{ type.label }}</h5>
              <p>{{ type.description }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 步骤2: 基本信息 -->
      <div v-if="currentStep === 2" class="step-panel">
        <h3>{{ $tt('插件基本信息') }}</h3>
        <div class="form-grid">
          <div class="form-group">
            <label>{{ $tt('插件名称 *') }}</label>
            <input 
              v-model="pluginForm.name" 
              type="text" 
              :placeholder="$tt('例如: my_music_plugin')"
              @input="validateName"
            >
            <div v-if="nameError" class="error-text">{{ nameError }}</div>
            <div class="help-text">{{ $tt('插件的内部标识名称，只能包含字母、数字和下划线') }}</div>
          </div>

          <div class="form-group">
            <label>{{ $tt('显示名称 *') }}</label>
            <input 
              v-model="pluginForm.display_name" 
              type="text" 
              :placeholder="$tt('例如: 我的音乐插件')"
            >
            <div class="help-text">{{ $tt('用户界面中显示的插件名称') }}</div>
          </div>

          <div class="form-group full-width">
            <label>{{ $tt('插件描述 *') }}</label>
            <textarea 
              v-model="pluginForm.description" 
              :placeholder="$tt('请描述插件的功能和用途...')"
              rows="3"
            ></textarea>
          </div>

          <div class="form-group">
            <label>{{ $tt('版本号') }}</label>
            <input 
              v-model="pluginForm.version" 
              type="text" 
              placeholder="1.0.0"
            >
          </div>

          <div class="form-group">
            <label>{{ $tt('作者') }}</label>
            <input 
              v-model="pluginForm.author" 
              type="text" 
              :placeholder="$tt('您的名称')"
            >
          </div>

          <div class="form-group">
            <label>{{ $tt('作者邮箱') }}</label>
            <input 
              v-model="pluginForm.author_email" 
              type="email" 
              placeholder="your@email.com"
            >
          </div>

          <div class="form-group">
            <label>{{ $tt('分类') }}</label>
            <input 
              v-model="pluginForm.category" 
              type="text" 
              :placeholder="$tt('例如: 音乐标签')"
            >
          </div>
        </div>
      </div>

      <!-- 步骤3: 配置项 -->
      <div v-if="currentStep === 3" class="step-panel">
        <h3>{{ $tt('插件配置项') }}</h3>
        <p>{{ $tt('定义插件运行时需要的配置参数') }}</p>

        <div class="config-items">
          <div 
            v-for="(item, index) in pluginForm.config_items" 
            :key="index"
            class="config-item"
          >
            <div class="config-item-header">
              <h4>{{ $tt('配置项 {index}', { index: index + 1 }) }}</h4>
              <button @click="removeConfigItem(index)" class="btn-remove">{{ $tt('删除') }}</button>
            </div>
            
            <div class="config-form">
              <div class="form-group">
                <label>{{ $tt('配置名称 *') }}</label>
                <input 
                  v-model="item.name" 
                  type="text" 
                  :placeholder="$tt('例如: api_key')"
                >
              </div>

              <div class="form-group">
                <label>{{ $tt('显示名称 *') }}</label>
                <input 
                  v-model="item.display_name" 
                  type="text" 
                  :placeholder="$tt('例如: API密钥')"
                >
              </div>

              <div class="form-group">
                <label>{{ $tt('类型 *') }}</label>
                <select v-model="item.type">
                  <option 
                    v-for="type in configTypes" 
                    :key="type.value" 
                    :value="type.value"
                  >
                    {{ type.label }}
                  </option>
                </select>
              </div>

              <div class="form-group full-width">
                <label>{{ $tt('描述') }}</label>
                <textarea 
                  v-model="item.description" 
                  :placeholder="$tt('配置项的说明...')"
                  rows="2"
                ></textarea>
              </div>

              <div class="form-group">
                <label>{{ $tt('默认值') }}</label>
                <input 
                  v-if="item.type !== 'bool'" 
                  v-model="item.default" 
                  type="text"
                >
                <select v-else v-model="item.default">
                  <option :value="true">{{ $tt('是') }}</option>
                  <option :value="false">{{ $tt('否') }}</option>
                </select>
              </div>

              <div class="form-group">
                <label>
                  <input v-model="item.required" type="checkbox">
                  {{ $tt('必需配置项') }}
                </label>
              </div>

              <!-- 选择类型的选项 -->
              <div v-if="item.type === 'select'" class="form-group full-width">
                <label>{{ $tt('选择选项') }}</label>
                <div class="options-editor">
                  <div 
                    v-for="(option, optIndex) in item.options" 
                    :key="optIndex"
                    class="option-item"
                  >
                    <input v-model="item.options[optIndex]" type="text">
                    <button @click="removeOption(item, optIndex)" class="btn-remove-small">×</button>
                  </div>
                  <button @click="addOption(item)" class="btn-add-option">{{ $tt('添加选项') }}</button>
                </div>
              </div>

              <!-- 数字类型的范围 -->
              <div v-if="item.type === 'number'" class="form-row">
                <div class="form-group">
                  <label>{{ $tt('最小值') }}</label>
                  <input v-model.number="item.min_value" type="number">
                </div>
                <div class="form-group">
                  <label>{{ $tt('最大值') }}</label>
                  <input v-model.number="item.max_value" type="number">
                </div>
              </div>
            </div>
          </div>

          <button @click="addConfigItem" class="btn-add-config">
            <i class="fas fa-plus"></i>
            {{ $tt('添加配置项') }}
          </button>
        </div>
      </div>

      <!-- 步骤4: 生成预览 -->
      <div v-if="currentStep === 4" class="step-panel">
        <h3>{{ $tt('插件代码预览') }}</h3>
        
        <div class="preview-actions">
          <button @click="generatePlugin" class="btn-generate" :disabled="generating">
            <i class="fas fa-cog" :class="{ 'fa-spin': generating }"></i>
            {{ generating ? $tt('生成中...') : $tt('生成插件代码') }}
          </button>
          
          <button v-if="generatedCode" @click="validateCode" class="btn-validate">
            <i class="fas fa-check"></i>
            {{ $tt('验证代码') }}
          </button>
        </div>

        <!-- 代码编辑器 -->
        <div v-if="generatedCode" class="code-editor-container">
          <div class="editor-toolbar">
            <span class="filename">{{ generatedFilename }}</span>
            <div class="toolbar-actions">
              <button @click="downloadPlugin" class="btn-download">
                <i class="fas fa-download"></i>
                {{ $tt('下载') }}
              </button>
              <button @click="installPlugin" class="btn-install">
                <i class="fas fa-upload"></i>
                {{ $tt('安装到系统') }}
              </button>
            </div>
          </div>
          
          <div class="code-editor">
            <pre><code>{{ generatedCode }}</code></pre>
          </div>
        </div>

        <!-- 验证结果 -->
        <div v-if="validationResult" class="validation-result">
          <h4>{{ $tt('代码验证结果') }}</h4>
          <div v-if="validationResult.is_valid" class="validation-success">
            <i class="fas fa-check-circle"></i>
            {{ $tt('代码验证通过！') }}
          </div>
          <div v-else class="validation-errors">
            <h5>{{ $tt('错误：') }}</h5>
            <ul>
              <li v-for="error in validationResult.errors" :key="error">{{ error }}</li>
            </ul>
          </div>
          <div v-if="validationResult.warnings.length > 0" class="validation-warnings">
            <h5>{{ $tt('警告：') }}</h5>
            <ul>
              <li v-for="warning in validationResult.warnings" :key="warning">{{ warning }}</li>
            </ul>
          </div>
        </div>

        <!-- 示例代码 -->
        <div class="example-section">
          <h4>{{ $tt('实现示例') }}</h4>
          <button @click="loadExample" class="btn-example">
            <i class="fas fa-lightbulb"></i>
            {{ $tt('查看实现示例') }}
          </button>
          
          <div v-if="exampleCode" class="example-code">
            <h5>{{ exampleCode.title }}</h5>
            <p>{{ exampleCode.description }}</p>
            <pre><code>{{ exampleCode.code }}</code></pre>
          </div>
        </div>
      </div>
    </div>

    <!-- 步骤导航按钮 -->
    <div class="step-navigation">
      <button 
        v-if="currentStep > 1" 
        @click="prevStep" 
        class="btn-prev"
      >
        <i class="fas fa-arrow-left"></i>
        {{ $tt('上一步') }}
      </button>
      
      <button 
        v-if="currentStep < 4" 
        @click="nextStep" 
        class="btn-next"
        :disabled="!canProceed"
      >
        {{ $tt('下一步') }}
        <i class="fas fa-arrow-right"></i>
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'PluginBuilder',
  data() {
    return {
      currentStep: 1,
      selectedTemplate: null,
      templates: [],
      pluginTypes: [],
      configTypes: [],
      generating: false,
      nameError: '',
      
      pluginForm: {
        name: '',
        display_name: '',
        description: '',
        version: '1.0.0',
        author: '',
        author_email: '',
        plugin_type: 'tag_source',
        category: '',
        config_items: []
      },
      
      generatedCode: '',
      generatedFilename: '',
      generatedManifest: null,
      validationResult: null,
      exampleCode: null
    }
  },
  
  computed: {
    canProceed() {
      switch (this.currentStep) {
        case 1:
          return this.selectedTemplate || this.pluginForm.plugin_type
        case 2:
          return this.pluginForm.name && this.pluginForm.display_name && this.pluginForm.description
        case 3:
          return true
        default:
          return true
      }
    }
  },
  
  mounted() {
    this.loadData()
  },
  
  methods: {
    async loadData() {
      try {
        // 加载模板列表
        const templatesResponse = await axios.get('http://127.0.0.1:8005/script/plugin-builder/templates/')
        this.templates = templatesResponse.data.data
        
        // 加载配置选项
        const schemaResponse = await axios.get('http://127.0.0.1:8005/script/plugin-builder/config-schema/')
        this.pluginTypes = schemaResponse.data.data.plugin_types
        this.configTypes = schemaResponse.data.data.config_types
        
      } catch (error) {
        const errorMessage = error?.message || this.$tt('未知错误')
        this.$message.error(this.$tt('加载数据失败: {message}', { message: errorMessage }))
      }
    },
    
    selectTemplate(templateId) {
      this.selectedTemplate = templateId
      this.pluginForm.plugin_type = this.templates.find(t => t.id === templateId)?.plugin_type || 'tag_source'
    },
    
    selectPluginType(type) {
      this.selectedTemplate = null
      this.pluginForm.plugin_type = type
    },
    
    validateName() {
      const name = this.pluginForm.name
      if (!name) {
        this.nameError = ''
        return
      }
      
      if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(name)) {
        this.nameError = this.$tt('插件名称只能包含字母、数字和下划线，且必须以字母开头')
      } else {
        this.nameError = ''
      }
    },
    
    addConfigItem() {
      this.pluginForm.config_items.push({
        name: '',
        display_name: '',
        type: 'text',
        default: '',
        description: '',
        required: false,
        options: []
      })
    },
    
    removeConfigItem(index) {
      this.pluginForm.config_items.splice(index, 1)
    },
    
    addOption(item) {
      if (!item.options) {
        item.options = []
      }
      item.options.push('')
    },
    
    removeOption(item, index) {
      item.options.splice(index, 1)
    },
    
    async generatePlugin() {
      try {
        this.generating = true
        
        const data = { ...this.pluginForm }
        if (this.selectedTemplate) {
          data.template_id = this.selectedTemplate
        }
        
        const response = await axios.post('http://127.0.0.1:8005/script/plugin-builder/generate/', data)
        
        this.generatedCode = response.data.data.plugin_code
        this.generatedFilename = response.data.data.filename
        this.generatedManifest = response.data.data.manifest
        
        this.$message.success(this.$tt('插件代码生成成功！'))
        
      } catch (error) {
        const errorMessage = error?.response?.data?.error || error?.message || this.$tt('未知错误')
        this.$message.error(this.$tt('生成插件失败: {message}', { message: errorMessage }))
      } finally {
        this.generating = false
      }
    },
    
    async validateCode() {
      try {
        const response = await axios.post('http://127.0.0.1:8005/script/plugin-builder/validate/', {
          plugin_code: this.generatedCode
        })
        
        this.validationResult = response.data.data
        
        if (this.validationResult.is_valid) {
          this.$message.success(this.$tt('代码验证通过！'))
        } else {
          this.$message.warning(this.$tt('代码存在问题，请查看验证结果'))
        }
        
      } catch (error) {
        const errorMessage = error?.message || this.$tt('未知错误')
        this.$message.error(this.$tt('验证失败: {message}', { message: errorMessage }))
      }
    },
    
    async downloadPlugin() {
      try {
        const response = await axios.post('http://127.0.0.1:8005/script/plugin-builder/download/', {
          plugin_code: this.generatedCode,
          filename: this.generatedFilename
        }, {
          responseType: 'blob'
        })
        
        // 创建下载链接
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', this.generatedFilename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        
        this.$message.success(this.$tt('下载成功！'))
        
      } catch (error) {
        const errorMessage = error?.message || this.$tt('未知错误')
        this.$message.error(this.$tt('下载失败: {message}', { message: errorMessage }))
      }
    },
    
    async installPlugin() {
      try {
        const response = await axios.post('http://127.0.0.1:8005/script/plugin-builder/install/', {
          plugin_code: this.generatedCode,
          manifest: this.generatedManifest
        })
        
        this.$message.success(this.$tt('插件安装成功！'))
        
      } catch (error) {
        const errorMessage = error?.response?.data?.error || error?.message || this.$tt('未知错误')
        this.$message.error(this.$tt('安装失败: {message}', { message: errorMessage }))
      }
    },
    
    async loadExample() {
      try {
        const response = await axios.get('http://127.0.0.1:8005/script/plugin-builder/examples/', {
          params: { type: this.pluginForm.plugin_type }
        })
        
        this.exampleCode = response.data.data
        
      } catch (error) {
        const errorMessage = error?.message || this.$tt('未知错误')
        this.$message.error(this.$tt('加载示例失败: {message}', { message: errorMessage }))
      }
    },
    
    getTemplateIcon(type) {
      return type === 'tag_source' ? 'fas fa-search' : 'fas fa-cogs'
    },
    
    getPluginTypeIcon(type) {
      return type === 'tag_source' ? 'fas fa-tags' : 'fas fa-layer-group'
    },
    
    nextStep() {
      if (this.canProceed && this.currentStep < 4) {
        this.currentStep++
      }
    },
    
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--
      }
    }
  }
}
</script>

<style scoped>
.plugin-builder {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.builder-header {
  text-align: center;
  margin-bottom: 30px;
}

.builder-header h2 {
  font-size: 2.5em;
  color: #2c3e50;
  margin-bottom: 10px;
}

.builder-header p {
  font-size: 1.1em;
  color: #7f8c8d;
}

/* 步骤导航 */
.steps-container {
  margin-bottom: 40px;
}

.steps {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 40px;
  position: relative;
}

.step:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 15px;
  left: 100%;
  width: 80px;
  height: 2px;
  background: #ddd;
  z-index: 1;
}

.step.completed:not(:last-child)::after {
  background: #27ae60;
}

.step-number {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #ddd;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-bottom: 8px;
  position: relative;
  z-index: 2;
}

.step.active .step-number {
  background: #3498db;
}

.step.completed .step-number {
  background: #27ae60;
}

.step-title {
  font-size: 0.9em;
  color: #7f8c8d;
}

.step.active .step-title {
  color: #2c3e50;
  font-weight: bold;
}

/* 步骤内容 */
.step-content {
  background: #fff;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  margin-bottom: 30px;
}

.step-panel h3 {
  font-size: 1.8em;
  color: #2c3e50;
  margin-bottom: 20px;
}

/* 模板和类型选择 */
.creation-method {
  margin-bottom: 40px;
}

.creation-method h4 {
  font-size: 1.3em;
  color: #34495e;
  margin-bottom: 15px;
}

.template-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.template-card, .type-card {
  border: 2px solid #ecf0f1;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.template-card:hover, .type-card:hover {
  border-color: #3498db;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(52, 152, 219, 0.2);
}

.template-card.selected, .type-card.selected {
  border-color: #2980b9;
  background: #ebf3fd;
}

.template-icon, .type-icon {
  font-size: 2.5em;
  color: #3498db;
  margin-bottom: 15px;
}

.template-card h5, .type-card h5 {
  font-size: 1.2em;
  color: #2c3e50;
  margin-bottom: 10px;
}

.template-card p, .type-card p {
  color: #7f8c8d;
  margin-bottom: 15px;
}

.template-type {
  background: #3498db;
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8em;
  display: inline-block;
}

.plugin-types {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

/* 表单样式 */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 5px;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 5px rgba(52, 152, 219, 0.3);
}

.help-text {
  font-size: 0.8em;
  color: #7f8c8d;
  margin-top: 5px;
}

.error-text {
  font-size: 0.8em;
  color: #e74c3c;
  margin-top: 5px;
}

/* 配置项 */
.config-items {
  margin-bottom: 20px;
}

.config-item {
  border: 1px solid #ecf0f1;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  background: #fafbfc;
}

.config-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.config-item-header h4 {
  color: #2c3e50;
  margin: 0;
}

.config-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  grid-column: 1 / -1;
}

.options-editor {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 10px;
  background: #fff;
}

.option-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.option-item input {
  flex: 1;
  margin-right: 10px;
}

/* 按钮样式 */
.btn-remove, .btn-remove-small {
  background: #e74c3c;
  color: #fff;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.btn-remove-small {
  padding: 2px 6px;
  font-size: 14px;
  font-weight: bold;
}

.btn-add-config, .btn-add-option {
  background: #27ae60;
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
}

.btn-add-config:hover, .btn-add-option:hover {
  background: #219a52;
}

/* 代码预览 */
.preview-actions {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
}

.btn-generate, .btn-validate, .btn-download, .btn-install, .btn-example {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
}

.btn-generate {
  background: #3498db;
  color: #fff;
}

.btn-validate {
  background: #f39c12;
  color: #fff;
}

.btn-download {
  background: #9b59b6;
  color: #fff;
}

.btn-install {
  background: #27ae60;
  color: #fff;
}

.btn-example {
  background: #34495e;
  color: #fff;
}

.btn-generate:disabled {
  background: #bdc3c7;
  cursor: not-allowed;
}

.code-editor-container {
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;
}

.editor-toolbar {
  background: #f8f9fa;
  padding: 10px 15px;
  border-bottom: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filename {
  font-weight: bold;
  color: #2c3e50;
}

.toolbar-actions {
  display: flex;
  gap: 10px;
}

.code-editor {
  max-height: 500px;
  overflow: auto;
  background: #f8f9fa;
}

.code-editor pre {
  margin: 0;
  padding: 15px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
}

/* 验证结果 */
.validation-result {
  background: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
}

.validation-success {
  color: #27ae60;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 8px;
}

.validation-errors, .validation-warnings {
  margin-top: 10px;
}

.validation-errors h5 {
  color: #e74c3c;
  margin-bottom: 5px;
}

.validation-warnings h5 {
  color: #f39c12;
  margin-bottom: 5px;
}

.validation-errors ul, .validation-warnings ul {
  margin: 0;
  padding-left: 20px;
}

/* 示例代码 */
.example-section {
  margin-top: 30px;
}

.example-code {
  background: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  margin-top: 15px;
}

.example-code h5 {
  color: #2c3e50;
  margin-bottom: 5px;
}

.example-code p {
  color: #7f8c8d;
  margin-bottom: 15px;
}

.example-code pre {
  background: #2c3e50;
  color: #fff;
  padding: 15px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 12px;
  line-height: 1.4;
}

/* 步骤导航 */
.step-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.btn-prev, .btn-next {
  padding: 12px 25px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.btn-prev {
  background: #95a5a6;
  color: #fff;
}

.btn-next {
  background: #3498db;
  color: #fff;
}

.btn-next:disabled {
  background: #bdc3c7;
  cursor: not-allowed;
}

.btn-prev:hover:not(:disabled) {
  background: #7f8c8d;
}

.btn-next:hover:not(:disabled) {
  background: #2980b9;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .config-form {
    grid-template-columns: 1fr;
  }
  
  .template-grid {
    grid-template-columns: 1fr;
  }
  
  .plugin-types {
    grid-template-columns: 1fr;
  }
  
  .steps {
    flex-direction: column;
    gap: 20px;
  }
  
  .step:not(:last-child)::after {
    display: none;
  }
}
</style> 
